import React, {
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useState,
} from 'react';
import {
  BryntumSchedulerPro,
  BryntumSchedulerProProps,
} from '@bryntum/schedulerpro-react';
import { PresetManager, ViewPreset, Tooltip } from '@bryntum/schedulerpro';
import { startOfDay, format } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
import {
  createDateWithTimeZoneOffset,
  getTimeWithOffset,
} from '@dispatch/shared';
import { useNotification } from '@dispatch/web/ui';
import { useSchedulerContext } from '../context/SchedulerContext';
import { useSchedulerConfig } from '../hooks/useSchedulerConfig';
import {
  useConflictDetection,
  applyDropValidationResult,
} from '../hooks/useConflictDetection';
import {
  SchedulerCoreProps,
  EventUpdateParams,
} from '../types/scheduler.types';
import {
  TooltipExtended,
  SchedulerProExtended,
  BeforeTaskEditShowPayload,
  ResourceNonWorkingTimeMouseOverPayload,
  TooltipBeforeShowPayload,
  BryntumAfterEventDropEvent,
  BryntumEventResizeEndEvent,
  BryntumBeforeEventDropFinalizeEvent,
  BryntumSelectionChangeEvent,
  BryntumEventClickEvent,
  BryntumBeforeEventEditEvent,
  BryntumBeforeTaskSaveEvent,
  BryntumBeforeEventDeleteEvent,
  BryntumBeforeEventDragEvent,
  BryntumEventDragValidatorContext,
} from '../types/bryntum.types';
import { getEventTooltipTemplate } from './EventTooltip';
import { SuperApiClaimOwnerCapacity } from '@dispatch/shared/types';
import {
  applyCapacityRegionFloor,
  CapacityRegionHost,
  CapacityScheduler,
  readClaimOwnerCapacity,
  refreshCapacityBands,
} from './CapacityBand';
import { mapSelectionToItems } from './selectionHandlers';
import { escapeHtml, unescapeHtml } from '../utils/escapeHtml';
import { coalesceFrame } from '../utils/coalesceFrame';
import {
  CAPACITY_DROP_REASON,
  isCapacityStripDrop,
  isCapacityStripTarget,
} from '../utils/capacityDropGuard';
import { mapDate } from '../helpers/mapDate';
import {
  WEEK_DAYS,
  addDaysTo,
  axisMatchesWindow,
  oneWeekWindow,
  planMenuEdit,
} from '../utils/displayWindow';
import type { DisplayWindow, MenuDateField } from '../utils/displayWindow';
import type { DateRangeMenuActions } from './DateRangeMenu';
import { useBryntumTheme } from '@dispatch/web/hooks';
// Bryntum v7: Base structural CSS (required) + Font Awesome icons.
// The theme stylesheet (material3-light/dark) is NOT imported here — it is
// loaded as a real, swappable <link> by useBryntumTheme so it can follow the
// active MUI palette mode. (A webpack import injects a <style>, which Bryntum's
// DomHelper.setTheme cannot find/swap.)
import '@bryntum/schedulerpro/schedulerpro.css';
import '@bryntum/schedulerpro/fontawesome/css/solid.css';
// Custom styles for icons, non-working time, etc.
import './styles.css';

/**
 * Static project configuration - defined outside component to prevent recreation.
 */
/**
 * Adapts our extended event handlers to Bryntum's generic prop types.
 *
 * Bryntum's prop types use base model classes (SchedulerEventModel, context: object, etc.)
 * while our handlers use extended interfaces with originalData/data fields.
 * At runtime the objects are identical — this adapter bridges the type gap in one place
 * instead of scattering `as unknown as` casts across JSX.
 */
function asBryntumProp<K extends keyof BryntumSchedulerProProps>(
  _key: K,
  handler: (...args: never[]) => unknown
): BryntumSchedulerProProps[K] {
  return handler as BryntumSchedulerProProps[K];
}

function getSchedulerInstance(
  ref: React.RefObject<{ instance?: unknown }>
): SchedulerProExtended | undefined {
  return ref.current?.instance as SchedulerProExtended | undefined;
}

const SELECTION_MODE = {
  row: true,
  cell: false,
  multiSelect: true,
  alwaysMultiSelect: false,
} as const;

const PROJECT_CONFIG = {
  silenceInitialCommit: true,
  stm: {
    autoRecord: true,
    disabled: true,
  },
  eventStore: {
    syncDataOnLoad: false,
    singleAssignment: true,
  },
  resourceStore: {
    syncDataOnLoad: true,
    useRawData: true,
  },
} as const;

/**
 * SchedulerCore - Clean Bryntum SchedulerPro wrapper
 */
export function SchedulerCore({
  resources,
  events,
  calendars,
  timeZone,
  dateRange,
  columnsState,
  canEditEvents,
  canCreateEvents,
  eventCategories,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  onDateRangeChange,
  onEventUpdate,
  onEventCreate,
  onSelectionChange,
  onEventSelect,
  onTaskEdit,
  onShift,
  onEventDelete,
  isAdjuster,
  isWorkingTimeFilteredState,
  workingDayStartHour,
  workingDayEndHour,
  showUnassignedGrid,
  onPendingEventChange,
  showClaimOwnerCapacity = false,
}: SchedulerCoreProps) {
  const { schedulerRef, registerShiftHandlers } = useSchedulerContext();
  const { notifyError, notifyWarning } = useNotification();

  // `useNotification` hands out new function identities every render. The
  // shift-handler effect must not re-run on them: its cleanup cancels the
  // pending scroll deferral, so the re-render a navigation itself causes would
  // cancel that navigation's scroll.
  const notifyWarningRef = useRef(notifyWarning);
  useEffect(() => {
    notifyWarningRef.current = notifyWarning;
  });

  /**
   * The header menu's Date range popup reaches the board through this ref
   * (ADO #109914). The inert first value is replaced by the shift-handler
   * effect before the board can be interacted with.
   */
  const dateRangeMenuActionsRef = useRef<DateRangeMenuActions>({
    getWindow: () => oneWeekWindow(dateRange.startDate, dateRange.endDate),
    applyMenuEdit: () => undefined,
    shiftPrevious: () => undefined,
    shiftToday: () => undefined,
    shiftNext: () => undefined,
  });

  // Load/swap the Bryntum theme <link> and set the global theme signal to
  // follow the active MUI palette mode.
  useBryntumTheme();

  const { createEventDragValidator } = useConflictDetection(schedulerRef);
  const initialConfigRef = useRef<ReturnType<typeof useSchedulerConfig> | null>(
    null
  );

  // Track if project is ready for drag operations
  // This prevents race conditions when data changes mid-drag
  const [isProjectReady, setIsProjectReady] = useState(true);
  const projectReadyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track whether a commitAsync is in-flight (ref avoids stale closure in cleanup)
  const commitInFlightRef = useRef(false);

  // Tooltip for non-working time intervals
  const nonWorkingTimeTooltipRef = useRef<TooltipExtended | null>(null);

  const currentConfig = useSchedulerConfig({
    eventCategories: eventCategories || [],
    columnsState,
    // Default to true - permissions may not be loaded on first render
    // and we capture config only once, so we need the right initial value
    canCreateEvents: canCreateEvents ?? true,
    isAdjuster: isAdjuster ?? false,
    timeZone,
    dateRangeMenuActionsRef,
  });

  // Bryntum Scheduler config is immutable after first render — runtime
  // permission changes (canCreateEvents, isAdjuster) are intentionally
  // ignored because Bryntum does not support dynamic config updates.
  if (initialConfigRef.current === null) {
    initialConfigRef.current = currentConfig;
  }

  const config = initialConfigRef.current;

  // Register the view preset with PresetManager on mount, clean up on unmount
  useEffect(() => {
    if (config.viewPreset) {
      const preset = new ViewPreset(config.viewPreset);
      PresetManager.add(preset);
    }
    return () => {
      PresetManager.remove('dispatchViewPreset');
    };
  }, [config.viewPreset]);

  useEffect(() => {
    const scheduler = getSchedulerInstance(schedulerRef);
    if (!scheduler) return;

    // If already painted, apply immediately
    if (scheduler.isPainted) {
      scheduler.viewPreset = 'dispatchViewPreset';
      return;
    }

    const onPaint = () => {
      scheduler.viewPreset = 'dispatchViewPreset';
      scheduler.un('paint', onPaint);
    };
    scheduler.on('paint', onPaint);

    return () => {
      scheduler.un?.('paint', onPaint);
    };
  }, [schedulerRef]);

  // Create tooltip for non-working time intervals (calendar data)
  useEffect(() => {
    const tooltip = new Tooltip({
      cls: 'scheduler-nonworkingtime-tooltip',
      html: '',
      autoShow: false,
    }) as TooltipExtended;

    nonWorkingTimeTooltipRef.current = tooltip;

    return () => {
      nonWorkingTimeTooltipRef.current?.destroy();
      nonWorkingTimeTooltipRef.current = null;
    };
  }, []);

  /**
   * The window the calendar is allowed to draw for the current React range.
   * Every path that pushes a timespan into Bryntum goes through this, so a
   * range wider than a week is clamped at the source rather than being drawn
   * and then corrected by the listener below.
   */
  const displayWindow = useMemo(
    () => oneWeekWindow(dateRange.startDate, dateRange.endDate),
    [dateRange.startDate, dateRange.endDate]
  );

  /**
   * The authoritative displayed window, readable from callbacks that must not
   * re-subscribe when it changes (the shift handlers, the clamp listener).
   * Navigation moves it; Bryntum never does.
   */
  const displayWindowRef = useRef(displayWindow);

  // Adopting the new window and pushing it to Bryntum is deliberately ONE
  // effect: the clamp listener below restores `displayWindowRef`, so if the ref
  // still held the previous window when `setTimeSpan` fired, the clamp would
  // "correct" the board straight back and fight the navigation.
  useEffect(() => {
    displayWindowRef.current = displayWindow;
    const scheduler = schedulerRef.current?.instance;
    if (scheduler && dateRange.startDate && dateRange.endDate) {
      scheduler.setTimeSpan(displayWindow.startDate, displayWindow.endDate);
    }
  }, [dateRange.startDate, dateRange.endDate, displayWindow]);

  /**
   * Keep the drawn axis on the intended week (ADO #104914).
   *
   * Zooming is not a pure rescale in Bryntum: it holds the rendered width
   * roughly constant and moves the DATE RANGE instead, so a zoom-out can both
   * widen the board past a week AND slide it onto a different week. Clamping the
   * span alone is not enough — that fixes the length while letting the anchor
   * drift, which silently shows the dispatcher a week they did not navigate to.
   * Restoring the whole window makes zoom what the AC asks for: the same dates
   * at more or fewer pixels per day.
   *
   * `zoomKeepsOriginalTimespan` is already on and is meant to preserve the
   * timespan across the zoom methods, so on that path this listener should find
   * nothing to do; it is the invariant that also covers preset changes and any
   * other path that moves the axis.
   *
   * Re-entry is guarded twice over, because `setTimeSpan` re-fires the very
   * events that call this:
   *  1. The `enforcing` flag swallows a synchronous re-fire from inside
   *     setTimeSpan.
   *  2. The early return makes the correction IDEMPOTENT — once the axis is the
   *     intended window there is nothing to do, so an asynchronous re-fire
   *     (after the flag has cleared) terminates instead of recurring. Both ends
   *     of the intended window sit on day boundaries so `autoAdjustTimeAxis`
   *     cannot perturb the axis into permanent disagreement.
   */
  useEffect(() => {
    const scheduler = getSchedulerInstance(schedulerRef);
    if (!scheduler?.on) return;

    let enforcing = false;

    const enforceOneWeek = () => {
      if (enforcing) return;

      const target = displayWindowRef.current;
      if (axisMatchesWindow(scheduler.startDate, scheduler.endDate, target)) {
        return;
      }

      enforcing = true;
      try {
        scheduler.setTimeSpan(target.startDate, target.endDate);
      } finally {
        enforcing = false;
      }
    };

    // Object form: all three subscriptions land in one call and are released by
    // the single detacher it returns, so a renamed event cannot silently leak a
    // listener the way three paired on()/un() calls can.
    const detach = scheduler.on({
      dateRangeChange: enforceOneWeek,
      timeAxisChange: enforceOneWeek,
      presetChange: enforceOneWeek,
    });

    return () => {
      if (typeof detach === 'function') detach();
    };
  }, [schedulerRef]);

  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as
      | SchedulerProExtended
      | undefined;
    const project = scheduler?.project;

    // Clear any pending timeout
    if (projectReadyTimeoutRef.current) {
      clearTimeout(projectReadyTimeoutRef.current);
    }

    // Mark project as not ready when data changes
    setIsProjectReady(false);

    commitInFlightRef.current = false;

    if (project?.commitAsync) {
      // Suspend refresh during batch data updates to prevent multiple redraws
      scheduler?.suspendRefresh?.();
      commitInFlightRef.current = true;

      // Commit any pending changes and mark ready when complete
      project
        .commitAsync()
        .then(() => {
          commitInFlightRef.current = false;
          if (scheduler && dateRange.startDate && dateRange.endDate) {
            const { startDate, endDate } = displayWindowRef.current;
            scheduler.setTimeSpan(startDate, endDate);
          }

          // Resume refresh after batch operations complete
          scheduler?.resumeRefresh?.(true);

          // Small delay to ensure Bryntum internals are fully settled
          projectReadyTimeoutRef.current = setTimeout(() => {
            setIsProjectReady(true);
          }, 100);
        })
        .catch(() => {
          commitInFlightRef.current = false;
          // Resume refresh even on error
          scheduler?.resumeRefresh?.(true);

          // On error, still mark as ready after delay to avoid permanent disable
          projectReadyTimeoutRef.current = setTimeout(() => {
            setIsProjectReady(true);
          }, 100);
        });
    } else {
      // If no project yet, mark ready after brief delay
      projectReadyTimeoutRef.current = setTimeout(() => {
        setIsProjectReady(true);
      }, 100);
    }

    return () => {
      if (projectReadyTimeoutRef.current) {
        clearTimeout(projectReadyTimeoutRef.current);
      }
      // If cleanup runs while commit is still in-flight, ensure refresh is resumed
      // to prevent the scheduler from being permanently suspended
      if (commitInFlightRef.current) {
        scheduler?.resumeRefresh?.(true);
      }
    };
  }, [events, resources, calendars, dateRange.startDate, dateRange.endDate]);

  // Sync calendar data imperatively when calendars change (timezone switch, etc.)
  // Bryntum's React wrapper doesn't reactively update the calendar manager store
  // when the calendars prop changes after initial load.
  useEffect(() => {
    const scheduler = getSchedulerInstance(schedulerRef);
    const calendarManager = (
      scheduler?.project as
        | { calendarManagerStore?: { data: unknown } }
        | undefined
    )?.calendarManagerStore;
    if (!calendarManager || !calendars?.length) return;

    calendarManager.data = calendars;
    scheduler?.refreshWithTransition?.();
  }, [calendars, schedulerRef]);

  // Timezone-aware current time indicator.
  // Bryntum's showCurrentTimeLine uses new Date() (browser local time) and doesn't
  // respect the API's timezone-adjusted display. We manage the current time line
  // manually via the timeRangeStore so it reflects the selected timezone.
  useEffect(() => {
    const scheduler = getSchedulerInstance(schedulerRef);
    if (!scheduler) return;

    // Disable Bryntum's built-in current time line (browser-local only)
    const timeRangesFeature = scheduler.features?.timeRanges as
      | { showCurrentTimeLine?: boolean }
      | undefined;
    if (timeRangesFeature) {
      timeRangesFeature.showCurrentTimeLine = false;
    }

    const timeRangeStore = scheduler.timeRangeStore as
      | {
          getById: (id: string) => { startDate: Date; name: string } | null;
          add: (data: Record<string, unknown>) => void;
          remove: (record: unknown) => void;
        }
      | undefined;
    if (!timeRangeStore) return;

    const updateCurrentTime = () => {
      // getTimeWithOffset returns a "fake" Date whose local time fields match
      // the target timezone's wall-clock time — exactly what the timezone-unaware
      // scheduler needs for correct positioning.
      const nowInTz = getTimeWithOffset(timeZone, new Date());
      const existing = timeRangeStore.getById('currentTimeLine');
      if (existing) {
        existing.startDate = nowInTz;
        existing.name = format(nowInTz, 'h:mm a');
      } else {
        timeRangeStore.add({
          id: 'currentTimeLine',
          name: format(nowInTz, 'h:mm a'),
          startDate: nowInTz,
          cls: 'b-sch-current-time',
        });
      }
    };

    const initTimeout = setTimeout(updateCurrentTime, 200);
    const interval = setInterval(updateCurrentTime, 10000);

    return () => {
      clearTimeout(initTimeout);
      clearInterval(interval);
      const existing = timeRangeStore.getById('currentTimeLine');
      if (existing) {
        timeRangeStore.remove(existing);
      }
    };
  }, [timeZone, schedulerRef]);

  /**
   * CLAIM OWNER CAPACITY BAND (ADO #104914)
   *
   * The band's per-day figures are positioned on the time axis in absolute
   * pixels, so they go stale on every shift and every zoom — Bryntum keeps the
   * rendered width roughly constant and changes the date range instead.
   * Rewriting the mounted bands in place is deliberate: RowExpander caches its
   * body elements per record, so collapsing and re-expanding would reuse stale
   * DOM rather than refresh it.
   */
  const capacityByResourceId = useMemo(() => {
    const map = new Map<string, SuperApiClaimOwnerCapacity | null>();
    resources.forEach((resource) => {
      if (resource.id === undefined || resource.id === null) return;
      map.set(String(resource.id), readClaimOwnerCapacity(resource));
    });
    return map;
  }, [resources]);

  /**
   * The capacity toggle (ADO #104914) reaches Bryntum through the RowExpander
   * feature's `disabled` flag on the LIVE instance. It cannot be a config-time
   * gate: `initialConfigRef` above freezes the config on first render, and the
   * toggle flips mid-session.
   *
   * `disabled = true` is the single off-path shared by "tenant flag off" and
   * "toggle off": Bryntum's `updateDisabled` hides the expander column and its
   * `doDisable` clears every expanded-row record and re-renders, so any band
   * already on screen goes away with the control.
   */
  useEffect(() => {
    const rowExpander =
      getSchedulerInstance(schedulerRef)?.features?.rowExpander;
    if (!rowExpander) return;

    rowExpander.disabled = !showClaimOwnerCapacity;
  }, [schedulerRef, showClaimOwnerCapacity]);

  /**
   * The same toggle floors how far the locked region can be dragged shut
   * (ADO #104914).
   *
   * The band's row labels live in the locked region, so a splitter dragged to
   * ~0px clipped them away while the time-axis half kept drawing figures —
   * leaving a dispatcher with three rows of bare numbers and nothing naming
   * them. Bryntum's RegionResize clamps each drag move to `subGrid.minWidth`,
   * so raising that floor is what keeps the labels reachable.
   *
   * Tied to the toggle rather than set in `subGridConfigs` so a capacity-off
   * board keeps today's behaviour exactly, including being able to shut the
   * region completely.
   *
   * No retry when the subgrids are not built yet: the toggle starts off on
   * every board load, so the first flip that needs a floor always happens long
   * after the grid has rendered.
   */
  useEffect(() => {
    applyCapacityRegionFloor(
      getSchedulerInstance(schedulerRef) as CapacityRegionHost | undefined,
      showClaimOwnerCapacity === true
    );
  }, [schedulerRef, showClaimOwnerCapacity]);

  useEffect(() => {
    // Nothing is mounted to refresh while the toggle is off, and the listeners
    // would fire on every shift and zoom for no reason.
    if (!showClaimOwnerCapacity) return;

    const scheduler = getSchedulerInstance(schedulerRef);
    if (!scheduler) return;

    /**
     * COALESCED, and coalesced HERE rather than per listener.
     *
     * All three subscriptions below already share this one callback, so
     * wrapping it once is what makes the guard cover every trigger — a bulk
     * expand (N × `rowExpand`), a zoom gesture (repeated `timeAxisChange`), and
     * the clamp's `setTimeSpan` re-firing `dateRangeChange` all land in the same
     * frame and produce ONE rewrite pass. Three separate debounces would be
     * three things to keep in step, and would still let a mixed burst (a zoom
     * during a bulk expand) do the work twice.
     *
     * `refreshCapacityBands` itself is untouched: it still walks every mounted
     * host and rewrites it in place, synchronously, when it runs. What changed
     * is only HOW OFTEN it runs. It reads the axis and the capacity map at call
     * time, so the single trailing pass renders the FINAL state of the burst.
     */
    const refresh = coalesceFrame(() =>
      refreshCapacityBands(
        scheduler as unknown as CapacityScheduler,
        (resourceId) => capacityByResourceId.get(resourceId) ?? null,
        // `today` defaults to now; the selected zone is what decides which
        // calendar day that is, exactly as it does for the current-time line.
        undefined,
        timeZone
      )
    );

    scheduler.on('timeAxisChange', refresh);
    scheduler.on('dateRangeChange', refresh);
    // A row expanded before the first paint has no axis to project onto yet.
    scheduler.on('rowExpand', refresh);
    refresh();

    return () => {
      // Before the detachers: a frame queued by the last trigger would
      // otherwise run after this effect has released the instance, rewriting
      // hosts on a board React has already torn down.
      refresh.cancel();
      scheduler.un?.('timeAxisChange', refresh);
      scheduler.un?.('dateRangeChange', refresh);
      scheduler.un?.('rowExpand', refresh);
    };
    // timeZone is a dependency on purpose: the Bryntum config (and so the
    // rowExpander renderer) is captured once on first render, so switching the
    // toolbar's zone can only reach the band through this in-place refresh.
  }, [schedulerRef, capacityByResourceId, timeZone, showClaimOwnerCapacity]);

  useEffect(() => {
    const scheduler = schedulerRef.current?.instance;
    if (scheduler?.features?.eventDragCreate) {
      scheduler.features.eventDragCreate.disabled = !isProjectReady;
    }
  }, [isProjectReady]);

  useEffect(() => {
    const scheduler = schedulerRef.current?.instance;
    const stm = scheduler?.project?.stm;
    if (stm && isProjectReady) {
      stm.enable();
    }
  }, [isProjectReady]);

  const handleBeforeTaskEditShow = useCallback(
    ({ editor, taskRecord }: BeforeTaskEditShowPayload) => {
      const categoryField = editor?.widgetMap?.categoryField as
        | {
            items?: string[];
            value?: string;
          }
        | undefined;
      if (categoryField) {
        const options = eventCategories ?? [];
        categoryField.items = options;
        const currentValue =
          taskRecord?.getData?.('category') ||
          (taskRecord?.data as Record<string, unknown> | undefined)?.[
            'category'
          ];
        if (currentValue) {
          // The category combo only offers the company's configured
          // categories. Match the event's stored value against them case-
          // and whitespace-insensitively and select the configured option
          // so it displays; never inject the raw value into the dropdown.
          const normalized = String(currentValue).trim().toLowerCase();
          const match = options.find(
            (option) => option.trim().toLowerCase() === normalized
          );
          if (match) {
            categoryField.value = match;
          }
        }
      }
    },
    [eventCategories]
  );

  const handleResourceNonWorkingTimeMouseOver = useCallback(
    ({
      resourceNonWorkingTimeRecord,
      domEvent,
    }: ResourceNonWorkingTimeMouseOverPayload) => {
      const tooltip = nonWorkingTimeTooltipRef.current;
      if (!tooltip) return;

      let text = resourceNonWorkingTimeRecord?.data?.cls || '';

      text = text.replace(/scheduler2\s*/gi, '').trim();

      if (text === ' ' || text === '') {
        text = 'No Schedules';
      }

      if (text) {
        tooltip.html = escapeHtml(text);
        tooltip.showBy({
          target: domEvent?.target ?? null,
          align: 't-b',
        });
      } else {
        tooltip.hide();
      }
    },
    []
  );

  const handleResourceNonWorkingTimeMouseOut = useCallback(() => {
    const tooltip = nonWorkingTimeTooltipRef.current;
    if (tooltip) {
      tooltip.hide();
    }
  }, []);

  // Cleanup-only handler — async finalization moved to handleBeforeEventDropFinalize
  const handleAfterEventDrop = useCallback(
    ({ context: _context }: BryntumAfterEventDropEvent) => {
      // Clean up constraint window highlights
      const scheduler = getSchedulerInstance(schedulerRef);
      if (constraintHighlightActiveRef.current && scheduler) {
        constraintHighlightActiveRef.current = false;
        scheduler.unhighlightTimeSpans?.();
        setTimeout(() => {
          scheduler.unhighlightTimeSpans?.();
        }, 100);
      }
    },
    [schedulerRef]
  );

  // Handle event resize
  const handleEventResizeEnd = useCallback(
    async ({ eventRecord, context }: BryntumEventResizeEndEvent) => {
      if (!canEditEvents) return;

      const params: EventUpdateParams = {
        eventId: eventRecord.data?.id || eventRecord.id?.toString() || '',
        oldResourceId: eventRecord.originalData?.resourceId || '',
        newResourceId: eventRecord.originalData?.resourceId || '',
        startDate: createDateWithTimeZoneOffset(
          context.startDate,
          timeZone
        ).toISOString(),
        endDate: createDateWithTimeZoneOffset(
          context.endDate,
          timeZone
        ).toISOString(),
        event: eventRecord.data,
      };

      context.async = true;
      try {
        await onEventUpdate(params);
        context.finalize(true);
      } catch (error) {
        if (process.env['NODE_ENV'] === 'development') {
          // eslint-disable-next-line no-console
          console.error('[SchedulerCore] Event resize failed:', error);
        }
        context.finalize(false);
      }
    },
    [canEditEvents, onEventUpdate, timeZone]
  );

  // Combined drag validator: constraint window + resource change + overlap
  const dragValidator = useMemo(
    () => createEventDragValidator(),
    [createEventDragValidator]
  );

  // Whether the pointer is currently over an expanded capacity strip.
  //
  // Tracked rather than read from the validator's context, because
  // BryntumEventDragValidatorContext carries no DOM reference — only dates and records. The listener
  // is installed for the life of a drag only (see handleBeforeEventDrag) so the scheduler's
  // pointermove path is untouched the rest of the time.
  //
  // Staleness is tolerable HERE and nowhere else: this drives the drag's appearance, which is
  // recomputed continuously, so a single stale tick is invisible. The binding decision — whether the
  // drop commits — never consults this, reading the released pointer from domEvent instead.
  const pointerOverCapacityStripRef = useRef(false);

  // Real-time validation during drag (Bryntum's validatorFn)
  // Context shape: { startDate, endDate, eventRecords[], newResource, ... }
  const eventDragValidatorFn = useCallback(
    (context: BryntumEventDragValidatorContext) => {
      // The invalid-drop treatment and its named reason (AC 19). The veto itself is on
      // beforeEventDropFinalize — validatorFn governs appearance only, and a guard placed here
      // alone would show the right cursor while still committing the drop.
      if (pointerOverCapacityStripRef.current) {
        return { valid: false, message: CAPACITY_DROP_REASON };
      }

      const eventRecord = context.eventRecords?.[0];
      if (!eventRecord) return { valid: true };

      const result = dragValidator({
        startDate: context.startDate,
        endDate: context.endDate,
        eventRecord,
        resourceRecord: {
          originalData: { id: context.newResource?.originalData?.id },
        },
        newResource: {
          originalData: { id: context.newResource?.originalData?.id },
        },
      });
      return result;
    },
    [dragValidator]
  );

  // Build eventDrag feature config with validatorFn
  const eventDragFeature = useMemo(
    () => ({
      ...config.features.eventDrag,
      validatorFn: eventDragValidatorFn,
    }),
    [config.features.eventDrag, eventDragValidatorFn]
  );

  // Track whether a constraint highlight is active so we can clear it reliably
  const constraintHighlightActiveRef = useRef(false);

  /**
   * Watches the pointer for the life of one drag, so validatorFn can show the invalid-drop
   * treatment over a capacity strip (AC 19).
   *
   * Scoped to the drag deliberately. A permanent pointermove listener on a scheduler this dense is a
   * cost paid on every mouse movement for a signal only a drag can use. Torn down on pointerup as
   * well as at drop, because a drag abandoned with Escape or released outside the grid never reaches
   * beforeEventDropFinalize and would otherwise leave the listener attached and the flag set —
   * which would make the NEXT drag start out believing it was over a strip.
   */
  const capacityPointerCleanupRef = useRef<(() => void) | null>(null);

  const stopTrackingCapacityPointer = useCallback(() => {
    capacityPointerCleanupRef.current?.();
    capacityPointerCleanupRef.current = null;
    pointerOverCapacityStripRef.current = false;
  }, []);

  const startTrackingCapacityPointer = useCallback(() => {
    stopTrackingCapacityPointer();

    const onPointerMove = (event: PointerEvent | MouseEvent) => {
      pointerOverCapacityStripRef.current = isCapacityStripTarget(event.target);
    };
    const onPointerUp = () => stopTrackingCapacityPointer();

    document.addEventListener('pointermove', onPointerMove, true);
    document.addEventListener('pointerup', onPointerUp, true);

    capacityPointerCleanupRef.current = () => {
      document.removeEventListener('pointermove', onPointerMove, true);
      document.removeEventListener('pointerup', onPointerUp, true);
    };
  }, [stopTrackingCapacityPointer]);

  // Released on unmount too: a drag interrupted by navigation would otherwise leak both listeners.
  useEffect(() => stopTrackingCapacityPointer, [stopTrackingCapacityPointer]);

  // Highlight constraint window when internal drag starts
  const handleBeforeEventDrag = useCallback(
    ({ eventRecord }: BryntumBeforeEventDragEvent) => {
      startTrackingCapacityPointer();

      const scheduler = getSchedulerInstance(schedulerRef);
      if (!scheduler) return;

      const original = eventRecord.originalData;
      const isTask =
        original?.taskReferenceId && original.taskReferenceId !== '';
      if (!isTask) return;

      const mustStartAfter = original?.mustStartAfter;
      const mustEndBefore = original?.mustEndBefore;
      if (!mustStartAfter || !mustEndBefore) return;

      constraintHighlightActiveRef.current = true;
      scheduler.highlightTimeSpans?.([
        {
          name: '',
          cls: 'constraint-window',
          startDate: new Date(mustStartAfter),
          endDate: new Date(mustEndBefore),
        },
      ]);
    },
    [schedulerRef, startTrackingCapacityPointer]
  );

  // Validate and async-finalize before drop — prevents Bryntum from committing
  // the drop until the reschedule modal is confirmed or cancelled.
  // Using beforeEventDropFinalize (not afterEventDrop) ensures finalize(false)
  // properly reverts the event to its original position.
  const handleBeforeEventDropFinalize = useCallback(
    async ({ context, domEvent }: BryntumBeforeEventDropFinalizeEvent) => {
      // Step 0: an expanded capacity strip is never a drop target (AC 19).
      //
      // Ahead of every other check, and resolved from domEvent rather than from context: the strip
      // lives in the row-expander body of the resource ABOVE it, so Bryntum has already resolved
      // context.newResource to that resource by the time we get here. Reading the context would
      // therefore confirm a perfectly valid-looking drop onto exactly the resource requirement 16
      // says the task must not be attributed to. Only the released pointer position distinguishes
      // the two, and domEvent is the one place that carries it.
      if (isCapacityStripDrop(domEvent)) {
        stopTrackingCapacityPointer();
        context.valid = false;
        context.finalize(false);
        return;
      }

      stopTrackingCapacityPointer();

      // Step 1: Synchronous validation
      const result = dragValidator({
        startDate: context.startDate,
        endDate: context.endDate,
        eventRecord: context.eventRecord,
        resourceRecord: context.resourceRecord,
        newResource: context.newResource,
      });

      if (!applyDropValidationResult(context, result)) {
        return;
      }

      if (!canEditEvents) return;

      // Step 2: Mark as async BEFORE Bryntum commits the drop
      context.async = true;

      const eventRecord = context.eventRecords?.[0] || context.eventRecord;
      const oldResourceId = eventRecord?.originalData?.resourceId || '';
      const newResourceId =
        context.newResource?.originalData?.id || oldResourceId;

      const params: EventUpdateParams = {
        eventId:
          eventRecord?.originalData?.id ||
          eventRecord?.data?.id ||
          eventRecord?.id?.toString() ||
          '',
        oldResourceId,
        newResourceId,
        startDate: createDateWithTimeZoneOffset(
          context.startDate,
          timeZone
        ).toISOString(),
        endDate: createDateWithTimeZoneOffset(
          context.endDate,
          timeZone
        ).toISOString(),
        event: eventRecord?.originalData ?? eventRecord?.data,
      };

      try {
        await onEventUpdate(params);
        context.finalize(true);
      } catch {
        context.finalize(false);
      }
    },
    [
      dragValidator,
      canEditEvents,
      onEventUpdate,
      timeZone,
      stopTrackingCapacityPointer,
    ]
  );

  // Clean up constraint highlights when drag is aborted (ESC or invalid drop)
  const handleEventDragAbort = useCallback(() => {
    if (!constraintHighlightActiveRef.current) return;
    constraintHighlightActiveRef.current = false;
    const scheduler = schedulerRef.current?.instance as
      | SchedulerProExtended
      | undefined;
    scheduler?.unhighlightTimeSpans?.();
    setTimeout(() => {
      scheduler?.unhighlightTimeSpans?.();
    }, 100);
  }, [schedulerRef]);

  const handleSelectionChange = useCallback(
    (event: BryntumSelectionChangeEvent) => {
      if (!onSelectionChange) return;

      // Trust Bryntum's native selection behavior driven by SELECTION_MODE
      // (multiSelect + alwaysMultiSelect: false): plain click replaces,
      // Ctrl/Cmd/Shift click adds to the selection. `event.selection` is the
      // authoritative current state after that logic has run. A previous
      // workaround tried to detect modifiers off `event.event` to gate a
      // manual `deselectAll`, but Bryntum does not reliably populate the DOM
      // event on selectionChange, which made every Ctrl+Click collapse back
      // to single-select. Mapping logic lives in `selectionHandlers` so the
      // pure transform is unit-testable without mounting the full scheduler.
      onSelectionChange(mapSelectionToItems(event.selection));
    },
    [onSelectionChange]
  );

  const handleEventClick = useCallback(
    ({ eventRecord }: BryntumEventClickEvent) => {
      const eventData = eventRecord?.data || eventRecord?.originalData;

      // Tasks have taskReferenceId, blocked time events don't
      const taskReferenceId = eventData?.taskReferenceId;
      const isTask = taskReferenceId && taskReferenceId !== '';

      // Only handle tasks - blocked time events do nothing on single click
      if (!isTask) {
        return;
      }

      if (onEventSelect) {
        onEventSelect({
          ...eventData,
          id: taskReferenceId,
          isEventClick: true,
          lossLat: eventData.lossLat,
          lossLong: eventData.lossLong,
        });
      }
    },
    [onEventSelect]
  );

  const handleBeforeEventEdit = useCallback(
    ({ eventRecord }: BryntumBeforeEventEditEvent) => {
      if (eventRecord.isPhantom) {
        return true;
      }

      const taskReferenceId =
        eventRecord?.getData?.('taskReferenceId') ||
        eventRecord?.data?.taskReferenceId ||
        eventRecord?.originalData?.taskReferenceId;

      if (taskReferenceId && taskReferenceId !== '') {
        if (onTaskEdit) {
          onTaskEdit(taskReferenceId as string);
        }
        return false;
      }
      return true;
    },
    [onTaskEdit]
  );

  const handleBeforeTaskSave = useCallback(
    async ({ taskRecord, editor }: BryntumBeforeTaskSaveEvent) => {
      // Get resourceId from taskRecord - Bryntum stores it on the record itself
      const resourceId =
        taskRecord.resourceId?.toString() ||
        taskRecord.resource?.id?.toString() ||
        '';

      // Read dates directly from taskRecord (Bryntum updates these on the record)
      // V1 parity: fail loudly via mapDate when Bryntum hands us something
      // unexpected, instead of silently producing Invalid Date strings.
      // Catch the throw so it surfaces as a toast rather than an unhandled
      // rejection — the editor stays open and the user gets feedback.
      let startDateStr: string;
      let endDateStr: string;
      try {
        startDateStr = mapDate(taskRecord.startDate).toISOString();
        endDateStr = mapDate(taskRecord.endDate).toISOString();
      } catch (error) {
        notifyError(
          'Validation Error',
          'Cannot save — event has invalid start or end date.'
        );
        if (process.env['NODE_ENV'] === 'development') {
          // eslint-disable-next-line no-console
          console.error('[SchedulerCore] mapDate failed:', error);
        }
        return false;
      }

      // Read name directly from taskRecord (standard Bryntum field)
      const name = taskRecord.name || '';

      // While the editor is open its widgetMap holds the freshest form
      // values; getData() can lag if a combo hasn't finished committing.
      // Prefer the widget, fall back to the record.
      const widgetMap = (
        editor as unknown as {
          widgetMap?: Record<string, { value?: unknown }>;
        }
      )?.widgetMap;
      const readField = <T,>(field: string, fallback: T): T => {
        const widgetVal = widgetMap?.[field]?.value;
        if (widgetVal !== undefined && widgetVal !== null && widgetVal !== '') {
          return widgetVal as T;
        }
        const recordVal = taskRecord.getData?.(field);
        return (recordVal ?? fallback) as T;
      };

      const notes = readField<string>('notes', '');
      const category = readField<string>('category', '');
      const isAllDay = readField<boolean>('isAllDay', false);
      const isForced = readField<boolean>('isForced', false);

      if (taskRecord.isPhantom) {
        if (!onEventCreate) return true;

        // Generate the externalId upfront so the placeholder and the
        // real refetched event share a stable key. SchedulerPage uses
        // this id to swap the placeholder for the real record the
        // moment it lands — no time-tolerance matching needed.
        const externalId = uuidv4();

        // Seed a pending placeholder so the event doesn't visually
        // disappear between removing the phantom and the refetched real
        // event arriving. Same animation the drag-drop path uses.
        onPendingEventChange?.({
          // Prefix keeps the placeholder's Bryntum id distinct from the
          // real event's id so they don't collide before the real one
          // arrives.
          id: `pending-event-${externalId}`,
          name,
          startDate: mapDate(taskRecord.startDate),
          endDate: mapDate(taskRecord.endDate),
          resourceId,
          cls: 'b-sch-event-pending just-dropped',
          isPending: true,
          realTaskId: externalId,
          kind: 'event',
        });

        // Remove the phantom + close the editor immediately so the
        // placeholder is the only visible representation during the call.
        const scheduler = schedulerRef.current?.instance as
          | SchedulerProExtended
          | undefined;
        if (scheduler?.eventStore) {
          scheduler.eventStore.remove(taskRecord);
        }
        if (editor?.close) {
          editor.close();
        }

        try {
          await onEventCreate({
            externalId,
            resourceId,
            name,
            startDate: startDateStr,
            endDate: endDateStr,
            notes,
            category,
            isAllDay,
            isForced,
          });

          // Success: leave the placeholder on screen. SchedulerPage
          // clears it when the refetch delivers a matching real event,
          // which avoids the flicker a fixed timer produces when the
          // refetch takes longer than the timeout.
          return false;
        } catch (error) {
          // On failure clear the placeholder immediately; the onError
          // toast + invalidation in useEventHandlers handles the rest.
          onPendingEventChange?.(null);
          if (process.env['NODE_ENV'] === 'development') {
            // eslint-disable-next-line no-console
            console.error('[SchedulerCore] Event creation failed:', error);
          }
          return false;
        }
      }

      const eventId = taskRecord.id?.toString() || '';
      if (!eventId || !startDateStr || !endDateStr) {
        // Returning true would let Bryntum do a silent local-only save
        // (no API call) — exactly the "save did nothing" bug. Block the
        // save and tell the user why.
        notifyError(
          'Validation Error',
          'Cannot save — event is missing required fields.'
        );
        if (process.env['NODE_ENV'] === 'development') {
          // eslint-disable-next-line no-console
          console.warn('[SchedulerCore] Missing required fields:', {
            eventId,
            startDateStr,
            endDateStr,
          });
        }
        return false;
      }

      try {
        const eventData = {
          externalId: eventId,
          resourceId,
          name,
          startDate: startDateStr,
          endDate: endDateStr,
          notes,
          category,
          isAllDay,
          isForced,
        };

        await onEventCreate(eventData);

        if (editor?.close) {
          editor.close();
        }

        return false;
      } catch (error) {
        if (process.env['NODE_ENV'] === 'development') {
          // eslint-disable-next-line no-console
          console.error('[SchedulerCore] Event update failed:', error);
        }
        return false;
      }
    },
    [onEventCreate, schedulerRef, onPendingEventChange, notifyError]
  );

  const handleBeforeEventDelete = useCallback(
    async ({ eventRecords, source }: BryntumBeforeEventDeleteEvent) => {
      const [eventRecord] = eventRecords;
      if (!eventRecord) return true;

      const eventId = eventRecord.id?.toString() || '';
      const resourceId = eventRecord.resourceId?.toString() || '';

      if (source && eventId && resourceId) {
        try {
          await onEventDelete(eventId, resourceId);
          // Bryntum leaves the task editor open when delete is fired
          // from inside it — close it manually so the user isn't left
          // staring at a stale form after a successful delete.
          const editor = (
            source as unknown as {
              features?: { taskEdit?: { editor?: { close?: () => void } } };
            }
          ).features?.taskEdit?.editor;
          editor?.close?.();
          return true; // Success - let Bryntum remove from store
        } catch (error) {
          // Error handled by onEventDelete notification
          if (process.env['NODE_ENV'] === 'development') {
            // eslint-disable-next-line no-console
            console.error('[SchedulerCore] Event delete failed:', error);
          }
          return false; // Failed - keep event in store
        }
      }

      return true;
    },
    [onEventDelete]
  );

  const lastScrollY = useRef(0);
  const loadMoreTriggered = useRef(false);
  const hasNextPageRef = useRef(hasNextPage);
  const isFetchingNextPageRef = useRef(isFetchingNextPage);

  useEffect(() => {
    hasNextPageRef.current = hasNextPage;
  }, [hasNextPage]);

  useEffect(() => {
    isFetchingNextPageRef.current = isFetchingNextPage;
    if (!isFetchingNextPage) {
      loadMoreTriggered.current = false;
    }
  }, [isFetchingNextPage]);

  useEffect(() => {
    const scheduler = schedulerRef.current?.instance;
    if (!scheduler || !onLoadMore) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const handleScroll = () => {
      if (debounceTimer) clearTimeout(debounceTimer);

      debounceTimer = setTimeout(() => {
        const extended = getSchedulerInstance(schedulerRef);
        const scrollable = extended?.scrollable;
        if (!scrollable) return;

        const scrollTop = scrollable.y ?? 0;
        const scrollHeight = scrollable.scrollHeight ?? 0;
        const clientHeight = scrollable.clientHeight ?? 0;

        if (scrollTop === lastScrollY.current) return;
        lastScrollY.current = scrollTop;

        const threshold = 200;
        const nearBottom = scrollTop + clientHeight >= scrollHeight - threshold;

        if (
          nearBottom &&
          hasNextPageRef.current &&
          !isFetchingNextPageRef.current &&
          !loadMoreTriggered.current
        ) {
          loadMoreTriggered.current = true;
          onLoadMore();
        }
      }, 100);
    };

    scheduler.on('scroll', handleScroll);
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      if (scheduler?.un) {
        scheduler.un('scroll', handleScroll);
      }
    };
  }, [schedulerRef, onLoadMore]);
  const prevResourceCountRef = useRef(resources.length);
  const savedScrollRef = useRef<{ x: number; y: number } | null>(null);

  // Capture scroll position before DOM updates when resources grow (must run before restore)
  useLayoutEffect(() => {
    if (resources.length > prevResourceCountRef.current) {
      const scheduler = schedulerRef.current?.instance as
        | SchedulerProExtended
        | undefined;
      if (scheduler?.storeScroll) {
        savedScrollRef.current = scheduler.storeScroll() as {
          x: number;
          y: number;
        };
      } else if (scheduler?.scrollable) {
        savedScrollRef.current = {
          x: 0,
          y: scheduler.scrollable.y ?? lastScrollY.current,
        };
      }
    }
  }, [resources.length, schedulerRef]);

  useLayoutEffect(() => {
    if (
      savedScrollRef.current &&
      resources.length > prevResourceCountRef.current
    ) {
      const scheduler = getSchedulerInstance(schedulerRef);
      if (scheduler?.restoreScroll) {
        scheduler.restoreScroll(savedScrollRef.current);
      } else if (scheduler?.scrollable) {
        const scrollable = scheduler.scrollable as { y?: number };
        if (typeof scrollable.y !== 'undefined') {
          const element = scheduler.element?.querySelector(
            '.b-grid-body-container'
          );
          if (element) {
            element.scrollTop = savedScrollRef.current.y;
          }
        }
      }
      savedScrollRef.current = null;
    }
    prevResourceCountRef.current = resources.length;
  }, [resources.length, schedulerRef]);

  /**
   * DATE SHIFT HANDLERS
   *
   * Prev steps the anchor back exactly one week; Next continues from the shown
   * window's end, which for a full week is exactly seven days on and after a
   * narrower custom range shows the days that follow it instead of skipping
   * them (ADO #109914). Both push the new window to Bryntum and React, then
   * scroll to the appropriate edge. The header menu's Date range popup moves the
   * board through the same `applyDisplayWindow` path.
   *
   * Bryntum's `shiftPrevious`/`shiftNext` are deliberately NOT used (ADO
   * #104914): they move by the ACTIVE preset's `shiftIncrement`, and zooming
   * swaps the preset, so once a dispatcher has zoomed, "next week" walks forward
   * a single day. Deriving the window here keeps the step at seven days at every
   * zoom level.
   *
   * Both sinks are written from the SAME computed window, which is what keeps the
   * request range and the drawn axis in step — previously that came from reading
   * the range back out of Bryntum after the shift.
   */
  useEffect(() => {
    // V1 parity: defer scroll by ~100ms so Bryntum has time to relayout the
    // time axis before we scroll; otherwise the scroll briefly targets the
    // old viewport before snapping to the new range.
    const SCROLL_DEFERRAL_MS = 100;
    const scrollTimeouts: NodeJS.Timeout[] = [];

    /**
     * Move the board to `target`, keeping Bryntum, the clamp's target window
     * and React on the same values. The single apply path for toolbar
     * navigation and the header menu's Date range popup alike.
     */
    const applyDisplayWindow = (
      scheduler: SchedulerProExtended,
      target: DisplayWindow,
      scrollTo: () => void
    ) => {
      // Adopt the new window BEFORE moving the axis: setTimeSpan fires
      // dateRangeChange synchronously, and the clamp listener would otherwise
      // read the previous week as its target and undo the navigation.
      displayWindowRef.current = target;
      scheduler.setTimeSpan(target.startDate, target.endDate);

      scrollTimeouts.push(setTimeout(scrollTo, SCROLL_DEFERRAL_MS));
      onDateRangeChange({
        startDate: target.startDate,
        endDate: target.endDate,
      });
      onShift?.();
    };

    /** Move the board to the week starting `startDate`. */
    const goToWeek = (
      scheduler: SchedulerProExtended,
      startDate: Date,
      scrollTo: () => void
    ) => {
      applyDisplayWindow(
        scheduler,
        oneWeekWindow(startDate, addDaysTo(startDate, WEEK_DAYS)),
        scrollTo
      );
    };

    const handleShiftNext = () => {
      const scheduler = getSchedulerInstance(schedulerRef);
      if (!scheduler) return;

      // Next picks up where the shown window ends. For a full week that is
      // exactly seven days on; after a narrower custom range it shows the days
      // that follow it instead of skipping them.
      const nextStart = displayWindowRef.current.endDate;
      goToWeek(scheduler, nextStart, () =>
        scheduler.scrollToDate(scheduler.startDate, { block: 'start' })
      );
    };

    const handleShiftPrevious = () => {
      const scheduler = getSchedulerInstance(schedulerRef);
      if (!scheduler) return;

      const previousStart = addDaysTo(
        displayWindowRef.current.startDate,
        -WEEK_DAYS
      );
      goToWeek(scheduler, previousStart, () =>
        scheduler.scrollToDate(scheduler.endDate, { block: 'end' })
      );
    };

    const handleShiftToday = () => {
      const scheduler = getSchedulerInstance(schedulerRef);
      if (!scheduler) return;

      goToWeek(scheduler, startOfDay(new Date()), () =>
        scheduler.scrollToNow({ block: 'center' })
      );
    };

    /** One edit from the header menu's Date range popup (ADO #109914). */
    const handleMenuEdit = (field: MenuDateField, value: Date): void => {
      const { target, notice } = planMenuEdit({
        current: displayWindowRef.current,
        field,
        value,
      });
      // The popup only exists on a mounted board, so a missing instance is a
      // teardown race; the fields are reset to the drawn window either way.
      const scheduler = getSchedulerInstance(schedulerRef);
      if (target && scheduler) {
        applyDisplayWindow(scheduler, target, () =>
          scheduler.scrollToDate(scheduler.startDate, { block: 'start' })
        );
      }
      if (notice) {
        notifyWarningRef.current(notice.title, notice.message);
      }
    };

    registerShiftHandlers({
      shiftNext: handleShiftNext,
      shiftPrevious: handleShiftPrevious,
      shiftToday: handleShiftToday,
    });

    dateRangeMenuActionsRef.current = {
      getWindow: () => displayWindowRef.current,
      applyMenuEdit: handleMenuEdit,
      shiftPrevious: handleShiftPrevious,
      shiftToday: handleShiftToday,
      shiftNext: handleShiftNext,
    };

    return () => {
      scrollTimeouts.forEach(clearTimeout);
    };
  }, [schedulerRef, onDateRangeChange, registerShiftHandlers, onShift]);

  /**
   * NON-WORKING TIME FILTER
   *
   * Filters the time axis to hide non-working time periods based on calendar data.
   * When pressed, compresses the scheduler to show only working hours.
   */
  const filterNonWorkingTime = useCallback(
    (pressed: boolean) => {
      const instance = getSchedulerInstance(schedulerRef);
      if (!instance) return;

      if (pressed) {
        // Filter time axis to hide non-working time periods
        instance.timeAxis.filter((tick: { startDate: Date }) => {
          const tickStart = new Date(tick.startDate);
          const isNonWorking = calendars?.some((cal) =>
            cal.intervals?.some((interval) => {
              if (interval.isWorking === false) {
                const intervalStart = new Date(interval.startDate);
                const intervalEnd = new Date(interval.endDate);
                return tickStart >= intervalStart && tickStart < intervalEnd;
              }
              return false;
            })
          );

          return !isNonWorking;
        });

        // Disable visual non-working time display when filtering
        if (instance.features?.resourceNonWorkingTime) {
          instance.features.resourceNonWorkingTime.disabled = true;
        }

        // Apply working time constraints (SchedulerPage owns the priority chain)
        instance.workingTime = {
          fromDay: 0,
          toDay: 7,
          fromHour: workingDayStartHour ?? 5,
          toHour: workingDayEndHour ?? 21,
        };
      } else {
        // Clear filters and show all time
        instance.suspendRefresh();
        instance.timeAxis.clearFilters();

        if (instance.features?.resourceNonWorkingTime) {
          instance.features.resourceNonWorkingTime.disabled = false;
        }

        instance.workingTime = {
          fromDay: 0,
          toDay: 7,
          fromHour: 0,
          toHour: 24,
        };

        instance.resumeRefresh(false);
        instance.refreshWithTransition();
      }
    },
    [calendars, workingDayStartHour, workingDayEndHour, schedulerRef]
  );

  // Apply or clear filter when state changes
  useEffect(() => {
    const isFiltered = isWorkingTimeFilteredState?.isNonWorkingTimeFiltered;
    // Small timeout to ensure scheduler is ready
    const timeout = setTimeout(() => {
      filterNonWorkingTime(isFiltered ?? false);
    }, 100);
    return () => clearTimeout(timeout);
  }, [
    isWorkingTimeFilteredState?.isNonWorkingTimeFiltered,
    filterNonWorkingTime,
  ]);

  // Reapply filter after date shift if filter is active
  useEffect(() => {
    if (!isWorkingTimeFilteredState?.isNonWorkingTimeFiltered) {
      return;
    }
    // Small timeout to ensure scheduler is ready after date shift
    const timeout = setTimeout(() => {
      filterNonWorkingTime(true);
    }, 500);
    return () => clearTimeout(timeout);
  }, [
    dateRange,
    isWorkingTimeFilteredState?.isNonWorkingTimeFiltered,
    filterNonWorkingTime,
  ]);

  // Note: STM uses Bryntum defaults for transaction management during drag-create

  // Ref to store tooltip click handlers for cleanup
  const tooltipClickHandlersRef = useRef<Map<Element, () => void>>(new Map());

  // Handle tooltip show - attach click handlers for copy functionality
  const handleTooltipShow = useCallback(
    ({ source }: TooltipBeforeShowPayload) => {
      const tooltipEl = source?.element;
      if (!tooltipEl) return;

      // Clean up any existing handlers
      tooltipClickHandlersRef.current.forEach((handler, el) => {
        el.removeEventListener('click', handler);
      });
      tooltipClickHandlersRef.current.clear();

      // Add click handlers to each tooltip field for individual copy
      const fields = tooltipEl.querySelectorAll('.tooltip-field');
      fields.forEach((field: Element) => {
        const handler = async () => {
          const value = field.getAttribute('data-value') || '';
          try {
            await navigator.clipboard.writeText(value);
            // Show feedback
            const feedback = tooltipEl.querySelector('.tooltip-field-feedback');
            if (feedback) {
              feedback.classList.add('visible');
              setTimeout(() => feedback.classList.remove('visible'), 1500);
            }
          } catch {
            // Silently fail if clipboard access is denied
          }
        };
        field.addEventListener('click', handler);
        tooltipClickHandlersRef.current.set(field, handler);
      });

      // Add click handler for copy-all button
      const copyButton = tooltipEl.querySelector('.tooltip-copy-button');
      if (copyButton) {
        const handler = async () => {
          const tooltipContainer = tooltipEl.querySelector(
            '.scheduler-event-tooltip'
          );
          const copyDataStr = tooltipContainer?.getAttribute('data-copy-all');
          if (!copyDataStr) return;

          try {
            const copyData = JSON.parse(unescapeHtml(copyDataStr));
            const text = Object.entries(copyData)
              .map(([key, value]) => `${key}: ${value}`)
              .join('\n');
            await navigator.clipboard.writeText(text);
            // Show feedback
            const feedback = tooltipEl.querySelector('.tooltip-copy-feedback');
            if (feedback) {
              feedback.classList.add('visible');
              setTimeout(() => feedback.classList.remove('visible'), 3000);
            }
          } catch {
            // Silently fail if clipboard access is denied
          }
        };
        copyButton.addEventListener('click', handler);
        tooltipClickHandlersRef.current.set(copyButton, handler);
      }
    },
    []
  );

  // Handle tooltip hide - remove click handlers
  const handleTooltipHide = useCallback(() => {
    tooltipClickHandlersRef.current.forEach((handler, el) => {
      el.removeEventListener('click', handler);
    });
    tooltipClickHandlersRef.current.clear();
  }, []);

  // Event tooltip configuration - only show for tasks, not blocked time events
  const eventTooltipFeature = useMemo(
    () => ({
      template: getEventTooltipTemplate,
      cls: 'scheduler-event-tooltip-container',
      autoHide: false,
      listeners: {
        beforeShow: ({ source }: TooltipBeforeShowPayload) => {
          // Only show tooltip for tasks (have taskReferenceId), not blocked time events
          const eventData =
            source.eventRecord?.data || source.eventRecord?.originalData;
          const taskReferenceId = eventData?.taskReferenceId;
          const isTask = taskReferenceId && taskReferenceId !== '';
          return isTask;
        },
        show: handleTooltipShow,
        hide: handleTooltipHide,
      },
    }),
    [handleTooltipShow, handleTooltipHide]
  );

  // Extract viewPreset and features from config since we pass them separately in v7
  const { viewPreset, features, ...schedulerConfig } = config;

  return (
    <BryntumSchedulerPro
      ref={schedulerRef}
      id={showUnassignedGrid ? 'no-right-radius' : 'scheduler'}
      width="100%"
      {...schedulerConfig}
      resources={resources}
      events={events}
      calendars={calendars as unknown as BryntumSchedulerProProps['calendars']}
      // Note: startDate/endDate are managed via setTimeSpan in useEffect
      // to prevent race conditions when both change simultaneously
      // Note: timeZone is NOT passed to Bryntum because the API already returns
      // calendar/event data adjusted for the selected timezone. Passing timeZone
      // here would cause a double conversion (API adjusts + Bryntum adjusts).
      zoomKeepsOriginalTimespan={true}
      // Limit zoom: out to ~5 days visible, in to ~1 day visible
      // while keeping 7-day data span (scroll to see rest)
      minZoomLevel={13}
      maxZoomLevel={17}
      readOnly={!canEditEvents}
      selectionMode={SELECTION_MODE}
      // Bryntum v7: Features must be passed as individual props with Feature suffix
      dependenciesFeature={features.dependencies}
      cellEditFeature={features.cellEdit}
      nonWorkingTimeFeature={features.nonWorkingTime}
      resourceNonWorkingTimeFeature={features.resourceNonWorkingTime}
      timeSpanHighlightFeature={features.timeSpanHighlight}
      scheduleTooltipFeature={features.scheduleTooltip}
      eventResizeFeature={features.eventResize}
      eventDragFeature={eventDragFeature}
      eventBufferFeature={features.eventBuffer}
      eventDragCreateFeature={features.eventDragCreate}
      eventTooltipFeature={eventTooltipFeature}
      taskEditFeature={features.taskEdit}
      eventMenuFeature={features.eventMenu}
      scheduleMenuFeature={features.scheduleMenu}
      cellMenuFeature={features.cellMenu}
      headerMenuFeature={features.headerMenu}
      timeAxisHeaderMenuFeature={features.timeAxisHeaderMenu}
      timeRangesFeature={features.timeRanges}
      rowExpanderFeature={features.rowExpander}
      project={PROJECT_CONFIG}
      onBeforeEventDrag={asBryntumProp(
        'onBeforeEventDrag',
        handleBeforeEventDrag
      )}
      onEventDragAbort={asBryntumProp('onEventDragAbort', handleEventDragAbort)}
      onAfterEventDrop={asBryntumProp('onAfterEventDrop', handleAfterEventDrop)}
      onEventResizeEnd={asBryntumProp('onEventResizeEnd', handleEventResizeEnd)}
      onBeforeEventDropFinalize={asBryntumProp(
        'onBeforeEventDropFinalize',
        handleBeforeEventDropFinalize
      )}
      onSelectionChange={asBryntumProp(
        'onSelectionChange',
        handleSelectionChange
      )}
      onEventClick={asBryntumProp('onEventClick', handleEventClick)}
      onBeforeEventEdit={asBryntumProp(
        'onBeforeEventEdit',
        handleBeforeEventEdit
      )}
      onBeforeTaskEditShow={handleBeforeTaskEditShow}
      onBeforeTaskSave={asBryntumProp('onBeforeTaskSave', handleBeforeTaskSave)}
      onBeforeEventDelete={asBryntumProp(
        'onBeforeEventDelete',
        handleBeforeEventDelete
      )}
      onResourceNonWorkingTimeMouseOver={handleResourceNonWorkingTimeMouseOver}
      onResourceNonWorkingTimeMouseOut={handleResourceNonWorkingTimeMouseOut}
    />
  );
}
